import React, { FC, useRef, useState } from 'react'
import * as Yup from 'yup'

import DynamicForm from '@/components/DynamicForm'
import { Button } from '@/components/UiElements'
import { startNotif, closeNotif } from '@/components/DynamicForm/util/notifier'
import formView from '@/app/products/sku/[sku]/modelView/shapeProfileForm'
import loadShapeProfile from '@/app/products/sku/[sku]/modelView/dataMapping/loadShapeProfile'
import Splash from '@/components/splash/Splash'
import saveShapeProfile from '@/services/ProductService/saveShapeProfile'
import shapeProfileMapping from '@/app/products/sku/[sku]/modelView/dataMapping/shapeProfile'
import calculateHeight from '@/app/products/sku/[sku]/modelView/utils/calculateHeight'
import loadSTLAction from '@/app/products/sku/[sku]/modelView/actions/loadSTL'
import upload from '@/services/upload'
import { getCompanyId } from '@/utils/getCompanyId'
import STLViewerComponent from '@/components/Product3D/util/viewerHooks/internal/STLViewerComponent'

interface ShapeProfileFormProps {
  profile: any | null
  onUpdateSuccess: (created?: { id?: any; name?: string }) => void
  onCancel?: () => void
}

// Extended form view that adds "Total Height" field to the base shape profile form
const extendedFormView = (defaultValues: any) => {
  const base = formView(defaultValues)
  const totalHeightField = {
    name: 'total_height',
    label: 'Total Height (mm)',
    placeholder: 'Enter total height',
    type: 'Number',
    className: 'col-span-3 row-span-1',
    defaultValue: defaultValues?.total_height || null,
    validation: Yup.number().typeError('Total Height is required').moreThan(0, 'Must be greater than 0'),
    reset: false,
    clear: false,
  }
  // Insert after shape_profile_name (index 0)
  return [base[0], totalHeightField, ...base.slice(1)]
}

const ShapeProfileForm: FC<ShapeProfileFormProps> = ({ profile, onUpdateSuccess, onCancel }) => {
  const formRef = useRef<any>(null)
  const stlInputRef = useRef<HTMLInputElement>(null)
  const prevTotalHeight = useRef<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [stlBase64, setStlBase64] = useState<string | null>(null)
  const [stlDimensions, setStlDimensions] = useState<{ y: number; z: number } | null>(null)
  const [stlFileInfo, setStlFileInfo] = useState<{ name: string; size: number } | null>(null)
  const [isDraggingStl, setIsDraggingStl] = useState<boolean>(false)

  const isEditing = profile?.id && profile.id !== 'duplicate'
  const baseDefaults: any = isEditing ? loadShapeProfile(profile) : {}

  // When editing, calculate total_height from center_of_gravity_z
  if (isEditing && baseDefaults.center_of_gravity_z) {
    baseDefaults.total_height = String(Number(baseDefaults.center_of_gravity_z) * 2)
  }

  const defaultValues = { ...baseDefaults }

  const handleStlFile = (file: File | null) => {
    if (!file) return

    setStlFileInfo({ name: file.name, size: file.size })
    const reader = new FileReader()
    reader.onload = async () => {
      const base64 = reader.result as string
      setStlBase64(base64)
      try {
        const dimensions = calculateHeight(base64)
        setStlDimensions(dimensions)
        // Get current form values, then re-init form with STL dimensions baked into defaults
        // Using initial() resets validation to clean state (no red errors)
        const currentData = (await formRef.current?.getData(true)) || {}
        const cleaned: any = {}
        Object.entries(currentData).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') cleaned[k] = v
        })
        const updatedFormView = (dv: any) => extendedFormView({ ...dv, ...cleaned, stl: dimensions })
        formRef.current?.initial(updatedFormView)
      } catch (error) {
        console.error('Error parsing STL file:', error)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleStlUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleStlFile(e.target.files?.[0] || null)
    if (stlInputRef.current) stlInputRef.current.value = ''
  }

  const handleStlDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingStl(false)
    handleStlFile(e.dataTransfer.files?.[0] || null)
  }

  const formatFileSize = (size: number) => {
    if (!size) return ''
    const units = ['Bytes', 'KB', 'MB', 'GB']
    const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1)
    return `${(size / Math.pow(1024, index)).toFixed(2)} ${units[index]}`
  }

  // When total_height changes, update center_of_gravity_z = total_height / 2
  const handleFormChange = (data: any) => {
    const th = data?.total_height
    if (th !== prevTotalHeight.current) {
      prevTotalHeight.current = th
      if (th) {
        const cogZ = parseFloat((Number(th) / 2).toFixed(2))
        formRef.current?.setData({ ...data, center_of_gravity_z: String(cogZ) })
      }
    }
  }

  const handleSaveProfile = async () => {
    setLoading(true)
    const notifId = startNotif('Saving shape profile...')
    const data = await formRef?.current?.getData()

    if (data) {
      // Resolve the STL URL: a freshly uploaded file takes precedence, otherwise reuse the existing one.
      // Mirrors the create flow — we always (re)run the LumiaLase conversion so profile_points stays in sync.
      let stlUrl: string | null = profile?.stl_url || null
      let profilePoints: any = null

      try {
        if (stlBase64) {
          closeNotif(notifId, 'Uploading STL file...', 'info')
          const fileName = `shape_profile_${Date.now()}.stl`
          stlUrl = await upload({ file: stlBase64, fileName, folderName: 'stl_files' })
        }

        // Convert the STL via LumiaLase to (re)generate profile_points for both create and update
        if (stlUrl) {
          closeNotif(notifId, 'Converting STL data...', 'info')
          profilePoints = await loadSTLAction(stlUrl)
        }
      } catch (error) {
        console.error('Error processing STL:', error)
        closeNotif(notifId, 'Failed to process STL file', { type: 'error' })
        setLoading(false)
        return
      }

      // Calculate center_of_gravity_z from total_height (divide by 2)
      if (data.total_height) {
        data.center_of_gravity_z = parseFloat((Number(data.total_height) / 2).toFixed(2))
      }

      const mappedData: any = await shapeProfileMapping(data, {
        product: {},
        uploadSTL: stlUrl,
        // Freshly converted points if we ran LumiaLase, otherwise fall back to the existing ones
        loadSTL: profilePoints || profile?.profile_points,
      })

      // Rename camera_job_id to camera_job_name_id and add company_id for standalone shape profile API
      const { camera_job_id, ...restLogoParams } = mappedData.logo_parameters || {}
      mappedData.logo_parameters = { ...restLogoParams, camera_job_name_id: camera_job_id }
      mappedData.company_id = getCompanyId()
      // The shape profile's 3D model IS the STL file, so point 3d_model_url at the STL URL.
      // (Sending a non-model placeholder gets rejected by the backend as invalid_three_d_image_url.)
      mappedData['3d_model_url'] = mappedData.stl_url || profile?.stl_url || profile?.['3d_model_url']

      if (isEditing) {
        mappedData.id = profile.id
      }

      const result = await saveShapeProfile(mappedData)
      if (result && !(result instanceof Error)) {
        closeNotif(notifId, 'Shape profile saved successfully', 'success')
        // Surface the created profile so the caller can auto-select it in a dropdown
        onUpdateSuccess({ id: result?.id ?? profile?.id, name: mappedData.name })
      } else {
        closeNotif(notifId, 'Failed to save shape profile', { type: 'error' })
      }
    } else {
      closeNotif(notifId, 'Validation failed', { type: 'error' })
    }
    setLoading(false)
  }

  return (
    <div className="flex flex-row gap-xl w-[1000px] max-w-[calc(100vw-80px)]">
      {loading && <Splash />}
      {/* Left side: Upload + Form */}
      <div className={'flex-grow flex flex-col gap-xxxl py-xl px-xl max-w-[850px]'}>
        {/* STL File Upload (click or drag-and-drop) */}
        <div>
          <label className="block text-sm font-medium pb-xs">Upload STL File</label>
          <div
            onClick={() => stlInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDraggingStl(true)
            }}
            onDragLeave={() => setIsDraggingStl(false)}
            onDrop={handleStlDrop}
            className={`flex flex-col items-center justify-center gap-xs rounded-md border-2 border-dashed px-xl py-lg text-center cursor-pointer transition-all ${
              isDraggingStl ? 'border-gray-900 bg-gray-50' : 'border-gray-300 hover:border-gray-500'
            }`}
          >
            <input
              ref={stlInputRef}
              type="file"
              accept=".stl"
              onChange={handleStlUpload}
              className="hidden"
            />
            {stlFileInfo ? (
              <p className="text-sm text-gray-900 break-all">
                {stlFileInfo.name}
                {stlFileInfo.size ? <span className="text-gray-400"> ({formatFileSize(stlFileInfo.size)})</span> : null}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Drag &amp; drop an STL file here, or click to browse</p>
            )}
          </div>
          {stlDimensions && (
            <p className="text-xs text-gray-500 pt-xs">
              Height: {stlDimensions.y.toFixed(2)}mm, Depth: {stlDimensions.z.toFixed(2)}mm
            </p>
          )}
          {/* File inputs can't be pre-filled — show the existing STL so it's clear a re-upload isn't required */}
          {!stlBase64 && profile?.stl_url && (
            <p className="text-xs text-gray-500 pt-xs">
              Current file:{' '}
              <a
                href={profile.stl_url}
                target="_blank"
                rel="noreferrer"
                className="text-primary-500 underline break-all"
              >
                {decodeURIComponent(profile.stl_url.split('/').pop() || 'existing STL')}
              </a>
              . Upload a new file only if you want to replace it.
            </p>
          )}
        </div>

        {/* Shape Profile Form */}
        <div className="grid grid-cols-12 gap-md">
          {/* @ts-ignore - DynamicForm is a JS component without TS declarations */}
          <DynamicForm ref={formRef} items={extendedFormView} defaultValues={defaultValues} size={'md'} onChange={handleFormChange} />
        </div>

        <div className="flex justify-end gap-md">
          {onCancel && (
            <Button size="lg" type="alt" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button size="lg" onClick={handleSaveProfile} disabled={loading}>
            {isEditing ? 'Update Shape Profile' : 'Add Shape Profile'}
          </Button>
        </div>
      </div>

      {/* Right side: STL 3D Preview — newly picked file, or the profile's existing STL on edit */}
      {(stlBase64 || profile?.stl_url) && (
        <div className="sticky top-[100px] w-[350px] h-[350px] flex-shrink-0 mt-xl">
          <STLViewerComponent url={stlBase64 || profile.stl_url} />
        </div>
      )}
    </div>
  )
}

export default ShapeProfileForm
